import { CategoryAxis, ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { Marker, px } from "@/components/tool-kit/charts/marks";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_WIDTH,
  cartesianFrame,
  heightForLabels,
  layoutCategoryLabels,
  rotatedOverflow,
} from "@/lib/tool-kit/charts/layout";
import { bandScale, linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import {
  type ErrorMeasure,
  errorHalf,
  type GroupStats,
  valueRange,
} from "@/lib/tool-kit/diagrams-more/error-bars";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * Group means with error bars, as columns or as dots. Columns start at zero — a column's
 * height IS its mean — while a dot chart fits its axis to the means and their bars. Every
 * bar is drawn both ways from the mean with caps, in dark ink over a white under-stroke
 * so it stays visible across a coloured column. The kind of bar is written in the image
 * itself, above the plot, so a chart copied without its caption is still labelled.
 */

const BAND = { paddingInner: 0.35, paddingOuter: 0.2 };
const TICK_STYLE = { fontSize: CHART_FONT_SIZE.tick };

export type ErrorBarStyle = "bars" | "dots";

export function ErrorBarChart({
  id,
  stats,
  measure,
  kind,
  barLabel,
  style,
  fmt,
  label,
  description,
}: {
  id: string;
  stats: readonly GroupStats[];
  measure: ErrorMeasure;
  kind: ErrorBarStyle;
  /** What the bars are, written above the plot: "Error bars: 95% CI". */
  barLabel: string;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const [low, high] = valueRange(stats, measure);
  const axis = niceTicks(low, high, { includeZero: kind === "bars" });
  const tick = tickFormat(fmt, axis.decimals);
  const input = {
    width: CHART_WIDTH,
    title: style.apa ? "" : style.title,
    xTitle: style.xTitle,
    yTitle: style.yTitle,
    leftLabelWidth: maxTextWidth(axis.ticks.map(tick), TICK_STYLE),
    topPadding: style.valueLabels ? 34 : 26,
  };
  const probe = cartesianFrame({
    ...input,
    height: CHART_HEIGHT,
    bottomLabelHeight: 0,
  });
  const step = bandScale(stats.length, [0, probe.plot.width], BAND).step;
  const labels = layoutCategoryLabels(
    stats.map((entry) => entry.group.label),
    step
  );
  const sized = {
    ...input,
    height: heightForLabels(labels.height),
    bottomLabelHeight: labels.height,
  };
  const first = cartesianFrame(sized);
  const overflow = rotatedOverflow(
    first,
    labels,
    step * (BAND.paddingOuter + (1 - BAND.paddingInner) / 2)
  );
  const frame =
    overflow > 0
      ? cartesianFrame({
          ...sized,
          leftLabelWidth: input.leftLabelWidth + overflow,
        })
      : first;
  const { plot } = frame;
  const band = bandScale(stats.length, [plot.x, plot.x + plot.width], BAND);
  const y = linearScale([axis.min, axis.max], [plot.y + plot.height, plot.y]);
  const color = paletteColor(colorIndex(style.color), 0);
  const barWidth = band.bandwidth * 0.62;
  const cap = Math.min(14, Math.max(6, band.bandwidth * 0.22));
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      <ValueAxis
        apa={style.apa}
        format={tick}
        orient="left"
        plot={plot}
        scale={y}
        ticks={axis.ticks}
        zeroLine={kind === "bars"}
      />
      <CategoryAxis
        apa={style.apa}
        axisLine={kind === "dots"}
        labels={labels}
        orient="bottom"
        plot={plot}
        positions={stats.map((_, index) => band.center(index))}
        ticks
      />
      <text
        fill={CHART_INK.muted}
        fontSize={CHART_FONT_SIZE.value + 1}
        textAnchor="end"
        x={px(plot.x + plot.width)}
        y={px(plot.y - 10)}
      >
        {barLabel}
      </text>
      {stats.map((entry, index) => {
        const centre = band.center(index);
        const mean = entry.group.mean;
        const half = errorHalf(entry, measure);
        const top = y(mean + (half ?? 0));
        const bottom = y(mean - (half ?? 0));
        return (
          <g key={`group-${entry.group.label}`}>
            {kind === "bars" ? (
              <rect
                fill={color}
                height={px(Math.abs(y(0) - y(mean)))}
                width={px(barWidth)}
                x={px(centre - barWidth / 2)}
                y={px(Math.min(y(0), y(mean)))}
              />
            ) : null}
            {half === null ? null : (
              <g fill="none" strokeLinecap="round">
                {[
                  { stroke: CHART_INK.background, width: 4.5 },
                  { stroke: CHART_INK.text, width: 1.75 },
                ].map((pen) => (
                  <path
                    d={`M${px(centre - cap / 2)} ${px(top)}H${px(centre + cap / 2)}M${px(centre)} ${px(top)}V${px(bottom)}M${px(centre - cap / 2)} ${px(bottom)}H${px(centre + cap / 2)}`}
                    key={pen.stroke}
                    stroke={pen.stroke}
                    strokeWidth={pen.width}
                  />
                ))}
              </g>
            )}
            {kind === "dots" ? (
              <Marker
                fill={color}
                shape="circle"
                size={11}
                x={centre}
                y={y(mean)}
              />
            ) : null}
            {style.valueLabels ? (
              <text
                fill={CHART_INK.text}
                fontSize={CHART_FONT_SIZE.value}
                textAnchor="middle"
                x={px(centre)}
                y={px(mean >= 0 ? top - 6 : bottom + CHART_FONT_SIZE.value + 4)}
              >
                {valueText(fmt, mean, 2)}
              </text>
            ) : null}
          </g>
        );
      })}
    </ChartSvg>
  );
}
